/**
 * Goal Health — a pure, browser-side recommendation engine for a goal's
 * delivery signal.
 *
 * {@link computeGoalHealth} folds together the traceability rollup (linked-work
 * completion + remaining effort + blockers), milestone dates, and "estimate
 * drift" (divergence between the human-entered manualProgress and the measured
 * story-completion) into:
 *   - a recommended {@link GoalStatus} signal,
 *   - a 0..100 health score,
 *   - the contributing factors + human-readable reasons, and
 *   - a single "Vector recommendation" sentence.
 *
 * This is ADVISORY ONLY. The stored `goal.status` is always human-owned; the
 * detail view lets a human accept the recommendation or override it with a
 * note. Nothing here writes anything.
 *
 * Deliberately NOT a productivity/velocity/leaderboard metric: it measures a
 * single goal's delivery risk against its own linked work and dates — never
 * ranks people or teams.
 *
 * Pure TS — no React.
 */

import type { GoalTraceability } from './traceability';
import type { Goal, GoalStatus } from './types';

/** The factor inputs that drove the recommendation (for transparency). */
export interface GoalHealthFactors {
  /** Story-completion percentage from traceability. */
  completionPct: number;
  /** Count of blocked linked stories. */
  blockedStories: number;
  /** Remaining estimated effort (0 when unknown). */
  remainingEffort: number;
  /** Whether a next milestone date exists. */
  hasMilestone: boolean;
  /** Days until the next milestone (negative = overdue); undefined when none. */
  daysToMilestone?: number;
  /** Whether the milestone is overdue with work still outstanding. */
  milestoneOverdue: boolean;
  /**
   * Estimate drift: |manualProgress − measured completion| (0..100), or
   * undefined when manualProgress is not set. High drift lowers confidence.
   */
  estimateDrift?: number;
  /** Whether there is any linked engineering work to assess. */
  hasLinkedWork: boolean;
}

/** The advisory output of the health engine. */
export interface GoalHealthSignal {
  /** Recommended delivery signal. */
  recommended: GoalStatus;
  /** 0..100 composite health score (higher = healthier). */
  score: number;
  /** Confidence in the recommendation given data completeness. */
  confidence: 'low' | 'medium' | 'high';
  /** The factors that drove the recommendation. */
  factors: GoalHealthFactors;
  /** Bullet-point reasons (human-readable). */
  reasons: string[];
  /** A single Vector recommendation sentence. */
  recommendation: string;
  /** True when the recommended signal differs from the goal's stored status. */
  divergesFromStatus: boolean;
}

const MS_PER_DAY = 1000 * 60 * 60 * 24;

/** Whole days from now until an ISO date (negative when in the past). */
function daysUntil(iso: string): number | undefined {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return undefined;
  const now = new Date();
  return Math.round((d.getTime() - now.getTime()) / MS_PER_DAY);
}

/**
 * Computes the advisory health signal for a goal from its traceability rollup.
 */
export function computeGoalHealth(
  goal: Goal,
  trace: GoalTraceability,
): GoalHealthSignal {
  const hasLinkedWork = trace.totalStories > 0;
  const completionPct = trace.storyCompletionPct;

  const milestoneDate = goal.nextMilestone?.dueDate;
  const daysToMilestone = milestoneDate ? daysUntil(milestoneDate) : undefined;
  const hasMilestone = daysToMilestone !== undefined;
  const workOutstanding =
    (hasLinkedWork && completionPct < 100) ||
    (goal.manualProgress !== undefined && goal.manualProgress < 100);
  const milestoneOverdue =
    daysToMilestone !== undefined && daysToMilestone < 0 && workOutstanding;

  const estimateDrift =
    goal.manualProgress !== undefined && hasLinkedWork
      ? Math.abs(goal.manualProgress - completionPct)
      : undefined;

  const factors: GoalHealthFactors = {
    completionPct,
    blockedStories: trace.blockedStories,
    remainingEffort: trace.remainingEffort,
    hasMilestone,
    daysToMilestone,
    milestoneOverdue,
    estimateDrift,
    hasLinkedWork,
  };

  // --- Score -------------------------------------------------------------
  // Start from completion; subtract risk from blockers, overdue/near
  // milestones with outstanding work, and estimate drift.
  const effectiveCompletion = hasLinkedWork
    ? completionPct
    : (goal.manualProgress ?? 0);
  let score = effectiveCompletion;

  const reasons: string[] = [];

  if (trace.blockedStories > 0) {
    const penalty = Math.min(35, 12 + trace.blockedStories * 8);
    score -= penalty;
    reasons.push(
      `${trace.blockedStories} linked ${trace.blockedStories === 1 ? 'story is' : 'stories are'} blocked.`,
    );
  }

  if (milestoneOverdue) {
    score -= 30;
    reasons.push(
      `Next milestone${goal.nextMilestone?.label ? ` "${goal.nextMilestone.label}"` : ''} is overdue with work outstanding.`,
    );
  } else if (
    daysToMilestone !== undefined &&
    daysToMilestone >= 0 &&
    daysToMilestone <= 14 &&
    workOutstanding
  ) {
    score -= 12;
    reasons.push(
      `Milestone is ${daysToMilestone === 0 ? 'due today' : `due in ${daysToMilestone} day${daysToMilestone === 1 ? '' : 's'}`} with work still open.`,
    );
  }

  if (estimateDrift !== undefined && estimateDrift >= 25) {
    score -= 10;
    reasons.push(
      `Reported progress (${goal.manualProgress}%) diverges ${Math.round(estimateDrift)} pts from measured completion (${completionPct}%).`,
    );
  }

  if (hasLinkedWork && trace.remainingEffort > 0) {
    reasons.push(
      `~${trace.remainingEffort} ${trace.effortUnit} of estimated work remaining.`,
    );
  }

  score = Math.max(0, Math.min(100, Math.round(score)));

  // --- Map to a recommended signal --------------------------------------
  let recommended: GoalStatus;
  if (goal.status === 'COMPLETED' || completionPct === 100) {
    recommended = 'COMPLETED';
    if (completionPct === 100 && reasons.length === 0) {
      reasons.push('All linked engineering work is complete.');
    }
  } else if (!hasLinkedWork && goal.manualProgress === undefined) {
    recommended = 'NOT_STARTED';
    reasons.push('No linked engineering work or progress recorded yet.');
  } else if (milestoneOverdue || trace.blockedStories >= 2 || score < 35) {
    recommended = 'OFF_TRACK';
  } else if (trace.blockedStories > 0 || score < 65) {
    recommended = 'AT_RISK';
  } else {
    recommended = 'ON_TRACK';
    if (reasons.length === 0) {
      reasons.push('Linked work is progressing without blockers or overdue milestones.');
    }
  }

  // --- Confidence based on data completeness ----------------------------
  let confidence: GoalHealthSignal['confidence'];
  if (!hasLinkedWork) {
    confidence = 'low';
  } else if (trace.unestimatedStories > 0 || trace.unresolvedStoryIds.length > 0) {
    confidence = 'medium';
  } else {
    confidence = 'high';
  }

  const recommendation = buildRecommendation(
    recommended,
    factors,
    trace,
    confidence,
  );

  return {
    recommended,
    score,
    confidence,
    factors,
    reasons,
    recommendation,
    divergesFromStatus: recommended !== goal.status,
  };
}

/** Builds the single-sentence Vector recommendation. */
function buildRecommendation(
  recommended: GoalStatus,
  factors: GoalHealthFactors,
  trace: GoalTraceability,
  confidence: GoalHealthSignal['confidence'],
): string {
  const dataCaveat =
    confidence === 'low'
      ? ' Link engineering stories (and estimates) to sharpen this signal.'
      : trace.unestimatedStories > 0
        ? ` Estimate the ${trace.unestimatedStories} unestimated linked ${trace.unestimatedStories === 1 ? 'story' : 'stories'} to sharpen this signal.`
        : '';

  switch (recommended) {
    case 'COMPLETED':
      return 'Vector suggests marking this goal Completed — all linked work is done.';
    case 'OFF_TRACK': {
      const lead = factors.milestoneOverdue
        ? 'the milestone is overdue with work outstanding'
        : factors.blockedStories >= 2
          ? `${factors.blockedStories} linked stories are blocked`
          : 'completion is low relative to remaining work';
      return `Vector suggests Off track: ${lead}. Prioritise unblocking and re-baseline the milestone.${dataCaveat}`;
    }
    case 'AT_RISK': {
      const lead =
        factors.blockedStories > 0
          ? 'there is at least one blocker'
          : 'progress is behind where it should be';
      return `Vector suggests At risk: ${lead}. Address blockers and confirm the milestone is still achievable.${dataCaveat}`;
    }
    case 'NOT_STARTED':
      return `Vector suggests Not started — no linked work is underway yet.${dataCaveat}`;
    case 'ON_TRACK':
    default:
      return `Vector suggests On track: linked work is progressing without blockers or overdue milestones.${dataCaveat}`;
  }
}
